import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { AuthorizationError, NotFoundError } from '@back4app2/sdk';
import back4app2 from '../back4app2';
import { trackEvent } from '../utils/amplitude';

/**
 * The way back from Stripe's checkout page.
 *
 * `useContainerPurchase` sends a buyer to Stripe with the app's page as the
 * way back: `?stripe_session=<id>` when they paid (or started a payment that
 * clears later), `?stripe_checkout=canceled` when they gave up there. This
 * reads that once, confirms the payment — the webhook confirms it too, and
 * the server applies it once — says what happened, reports the end of the
 * funnel and takes the parameters out of the address.
 *
 * A buyer who gave up, closed the tab or never came back leaves an app that
 * waits for its payment and deploys nothing until then; its page offers to
 * pay or to continue on Free.
 */

const STORAGE_KEY = 'b4a:stripe-checkout';

type Tags = Record<string, unknown>;

/** The funnel tags of the checkout the browser is leaving for. */
export const rememberStripeCheckout = (sessionId: string, tags: Tags) => {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify({ sessionId, tags }));
  } catch (e) {
    // Private mode or blocked storage: the events go out with fewer tags.
  }
};

const recallStripeCheckout = (): { sessionId?: string; tags?: Tags } => {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);

    sessionStorage.removeItem(STORAGE_KEY);

    return raw ? JSON.parse(raw) : {};
  } catch (e) {
    return {};
  }
};

export default function useStripeCheckoutReturn(appId?: string) {
  const location = useLocation();
  const navigate = useNavigate();
  const handledRef = useRef<string>();

  useEffect(() => {
    if (!appId) {
      return;
    }

    const params = new URLSearchParams(location.search);
    const sessionId = params.get('stripe_session');
    const canceled = params.get('stripe_checkout') === 'canceled';

    if (!sessionId && !canceled) {
      return;
    }

    const key = sessionId || 'canceled';

    if (handledRef.current === key) {
      return;
    }

    handledRef.current = key;

    // Out of the address first: a reload, or the link copied from it, must not
    // run this again.
    params.delete('stripe_session');
    params.delete('stripe_checkout');
    navigate(
      {
        pathname: location.pathname,
        search: params.toString() ? `?${params.toString()}` : '',
        hash: location.hash,
      },
      { replace: true }
    );

    const remembered = recallStripeCheckout();
    const tags: Tags = remembered.tags || {
      app_id: appId,
      provider: 'stripe',
      variant: 'stripe_hosted',
    };

    if (canceled) {
      trackEvent('containers_checkout_closed', tags);
      toast('Checkout canceled. Nothing was charged — your app is waiting for its payment.');

      return;
    }

    back4app2
      .confirmContainerStripeCheckout(appId, sessionId as string)
      .then((outcome) => {
        trackEvent('containers_checkout_completed', { ...tags, outcome });

        if (outcome === 'PAID') {
          toast.success('Payment received. Your app is deploying.');
        } else if (outcome === 'PROCESSING') {
          toast.success('Your payment is processing. The app deploys as soon as it clears — this can take a few business days.');
        } else {
          toast("We're confirming your payment with Stripe. This page updates as soon as it's done.");
        }
      })
      .catch((e) => {
        // A refusal is not a hiccup: the session is not this account's, and
        // nothing is coming to apply it. Anything else, the webhook converges.
        if (e instanceof AuthorizationError || e instanceof NotFoundError) {
          console.error('the Stripe checkout was refused for this app', e);
          toast.error("The payment couldn't be applied to this app. Contact support and we'll sort it out.");

          return;
        }

        console.error('could not confirm the Stripe checkout yet', e);
        toast("We're confirming your payment with Stripe. This page updates as soon as it's done.");
      });
  }, [appId, location.search, location.pathname, location.hash, navigate]);
}
